// Enhanced Apollo client configuration
import {
  ApolloClient,
  InMemoryCache,
  Observable,
  createHttpLink,
  from,
} from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { CognitoUserPool } from "amazon-cognito-identity-js";
import { setContext } from "@apollo/client/link/context"; // Add this import

const userPool = new CognitoUserPool({
  UserPoolId: process.env.VITE_APP_USER_POOL_ID!,
  ClientId: process.env.VITE_APP_USER_POOL_CLIENT_ID!,
});

let isRefreshing = false;
let pendingRequests: Function[] = [];

const resolvePendingRequests = () => {
  pendingRequests.forEach((callback) => callback());
  pendingRequests = [];
};

const getCurrentToken = async (): Promise<string | null> => {
  return new Promise((resolve) => {
    const currentUser = userPool.getCurrentUser();
    if (currentUser) {
      currentUser.getSession((err: any, session: any) => {
        if (err || !session) {
          resolve(null);
          return;
        }
        resolve(session.getIdToken().getJwtToken());
      });
    } else {
      resolve(null);
    }
  });
};

const authLink = setContext(async (_, { headers }) => {
  const token = await getCurrentToken();

  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : "",
    },
  };
});

const errorLink = onError(
  ({ graphQLErrors, networkError, operation, forward }) => {
    if (graphQLErrors) {
      for (let err of graphQLErrors) {
        switch (err.extensions?.code) {
          case "UNAUTHENTICATED":
            if (!isRefreshing) {
              isRefreshing = true;

              // Try to refresh the token
              new Promise(async (resolve) => {
                const currentUser = userPool.getCurrentUser();
                if (currentUser) {
                  currentUser.getSession(async (err: any, session: any) => {
                    if (err || !session) {
                      // Redirect to login
                      window.location.href = "/login";
                      resolve(false);
                      return;
                    }

                    if (!session.isValid()) {
                      currentUser.refreshSession(
                        session.getRefreshToken(),
                        // (refreshErr, newSession) => {
                        (refreshErr) => {
                          if (refreshErr) {
                            window.location.href = "/login";
                            resolve(false);
                            return;
                          }
                          resolve(true);
                        }
                      );
                    } else {
                      resolve(true);
                    }
                  });
                } else {
                  window.location.href = "/login";
                  resolve(false);
                }
              }).then((success) => {
                isRefreshing = false;
                if (success) {
                  resolvePendingRequests();
                }
              });
            }

            return new Observable((observer) => {
              pendingRequests.push(() => {
                forward(operation).subscribe(observer);
              });
            });
        }
      }
    }

    if (networkError && (networkError as any).statusCode === 401) {
      // Handle 401 errors
      window.location.href = "/login";
    }
  }
);

const httpLink = createHttpLink({
  uri: process.env.VITE_APP_GRAPHQL_URL,
});

export const apolloClient = new ApolloClient({
  link: from([authLink, errorLink, httpLink]),
  cache: new InMemoryCache(),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: "cache-and-network",
      errorPolicy: "all",
    },
    query: {
      errorPolicy: "all",
    },
  },
});
